import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Loader2, ShoppingBag } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { RevealLineOnView } from "@/components/RevealLine";
import { useCart } from "@/context/CartContext";

const PLACEHOLDER = "All";

export default function ShopPage() {
  const [data, setData] = useState({ products: [], categories: [PLACEHOLDER], total: 0 });
  const [active, setActive] = useState(PLACEHOLDER);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    const inCat = active === PLACEHOLDER ? data.products : data.products.filter((p) => p.category === active);
    if (!query.trim()) return inCat;
    const q = query.trim().toLowerCase();
    return inCat.filter((p) => p.name.toLowerCase().includes(q));
  }, [data.products, active, query]);

  return (
    <div className="grain bg-[#050505] min-h-screen">
      <Nav />
      <main className="pt-32 md:pt-40">
        {/* Header */}
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 mb-16 md:mb-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-5 eyebrow">
              <RevealLineOnView>The Shop — Detailing Supplies</RevealLineOnView>
            </p>
            <div className="md:col-span-7">
              <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tight text-white leading-[0.9]">
                <RevealLineOnView>Everything we</RevealLineOnView>
                <RevealLineOnView delay={0.06} className="italic text-brand-red">
                  use in-studio.
                </RevealLineOnView>
              </h1>
              <p className="mt-8 text-neutral-400 text-base md:text-lg max-w-lg leading-relaxed">
                The same towels, pads, sprays and machines we trust on every
                vehicle — hand-picked, priced fair, and ready to ship. Tap any
                product to complete your purchase on our secure store.
              </p>
            </div>
          </div>
        </section>

        {/* Filter bar */}
        <section className="sticky top-16 md:top-20 z-30 backdrop-blur-xl bg-[#050505]/85 border-y border-white/5">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4 md:py-5 flex items-center gap-3 md:gap-6 overflow-x-auto no-scrollbar">
            {data.categories.map((c) => (
              <button
                key={c}
                data-testid={`shop-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`shrink-0 font-mono text-[10px] md:text-[11px] tracking-[0.24em] uppercase pb-1 border-b transition-colors ${
                  active === c
                    ? "text-white border-brand-red"
                    : "text-neutral-500 border-transparent hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
            <div className="ml-auto shrink-0 hidden md:block">
              <input
                data-testid="shop-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products…"
                className="bg-transparent border-b border-white/10 focus:border-white/40 outline-none text-sm text-white placeholder-neutral-600 w-56 py-2 transition-colors"
              />
            </div>
          </div>
        </section>

        {/* Grid */}
        <section
          data-testid="shop-grid"
          className="max-w-[1600px] mx-auto px-6 md:px-12 py-12 md:py-16"
        >
          {loading ? (
            <div className="py-40 flex items-center justify-center text-neutral-500">
              <Loader2 className="animate-spin mr-3" size={16} />
              <span className="font-mono text-[11px] tracking-[0.24em] uppercase">Loading catalog…</span>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between mb-8 md:mb-10">
                <p className="eyebrow">
                  {filtered.length} of {data.products.length} items · {active}
                </p>
                <p className="hidden md:block font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-600">
                  Free local pickup · Paralowie
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-10">
                <AnimatePresence mode="popLayout">
                  {filtered.map((p, i) => (
                    <ProductCard key={p.slug} p={p} idx={i} />
                  ))}
                </AnimatePresence>
              </div>

              {filtered.length === 0 && (
                <div className="py-32 text-center border border-dashed border-white/10">
                  <p className="font-serif text-3xl text-neutral-500">Nothing here yet.</p>
                  <p className="mt-2 text-neutral-600 text-sm">Try a different category or search term.</p>
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ProductCard({ p, idx }) {
  const hasSale = p.sale_price != null && p.sale_price < p.price;
  const { add } = useCart();
  return (
    <motion.article
      data-testid={`shop-product-${p.slug}`}
      layout
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.7, delay: (idx % 12) * 0.03, ease: [0.22, 1, 0.36, 1] }}
      className="group block"
    >
      <Link
        to={`/shop/${p.slug}`}
        data-testid={`shop-open-${p.slug}`}
        className="relative aspect-square overflow-hidden bg-[#0a0a0a] border border-white/5 group-hover:border-white/25 transition-colors duration-500 block"
      >
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.style.opacity = "0.15";
          }}
        />
        {p.badge && (
          <span className="absolute top-3 left-3 bg-black/70 backdrop-blur px-2.5 py-1 font-mono text-[9px] tracking-[0.22em] uppercase text-white">
            {p.badge}
          </span>
        )}
        {hasSale && (
          <span className="absolute top-3 right-3 bg-brand-red text-white px-2.5 py-1 font-mono text-[9px] tracking-[0.22em] uppercase">
            Sale
          </span>
        )}
        <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white text-black flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
          <ArrowUpRight size={14} />
        </span>
      </Link>
      <div className="pt-4">
        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500 mb-2">
          {p.category}
        </p>
        <Link
          to={`/shop/${p.slug}`}
          className="font-serif text-lg md:text-xl text-white leading-tight mb-3 group-hover:text-brand-red transition-colors duration-500 block"
        >
          {p.name}
        </Link>
        <div className="flex items-baseline gap-3 mb-4">
          {hasSale ? (
            <>
              <span className="font-serif text-2xl text-white">${p.sale_price.toFixed(2)}</span>
              <span className="font-mono text-xs text-neutral-500 line-through">${p.price.toFixed(2)}</span>
            </>
          ) : (
            <span className="font-serif text-2xl text-white">${p.price.toFixed(2)}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => add(p, 1)}
          data-testid={`shop-add-${p.slug}`}
          className="w-full flex items-center justify-center gap-2 py-3 border border-white/15 hover:border-white hover:bg-white hover:text-black transition-all font-mono text-[10px] tracking-[0.24em] uppercase text-white"
        >
          <ShoppingBag size={13} strokeWidth={1.4} /> Add to Cart
        </button>
      </div>
    </motion.article>
  );
}

// Hide horizontal scrollbar on filter chip row
const styleTag =
  typeof document !== "undefined" ? document.createElement("style") : null;
if (styleTag && !document.getElementById("no-scrollbar-style")) {
  styleTag.id = "no-scrollbar-style";
  styleTag.textContent = `.no-scrollbar::-webkit-scrollbar{display:none} .no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}`;
  document.head.appendChild(styleTag);
}
